import type { KrCategory, KrKeyword, KrMeasuredFrom, KrStat, KrSummaryItem } from '@elva/ui';
import { growthGet, growthGetAccepted, growthList, growthPostAsync } from '@/data/http';
import type { MarketingJobData, MarketingMeta } from '@/data/marketingApiTypes';
import { deepCamel, num, relativeTimeFromIso, str } from '@/data/adapt';

interface BeKeyword {
  id?: string;
  keyword?: string;
  search_volume_monthly?: number;
  category?: string;
  volume?: string | number;
  search_volume?: number;
  leader?: string;
  rank?: string | number | null;
  position?: number | null;
  delta?: number;
  rank_delta?: number;
  trend?: string;
  your_position?: number | null;
  your_position_change_30d?: number;
  best_competitor?: { competitor_id?: string; name?: string; position?: number };
  intent?: string;
  has_strategy_brief?: boolean;
  strategy_brief_expires_at?: string;
}

interface BeKeywordBrief {
  compared_to?: string;
  bottom_line?: string;
  doing_right?: string[];
  gaps?: string[];
  playbook?: string[];
  serp?: Array<Record<string, unknown>>;
}

interface BeKeywordSummary {
  measured_from?: { location?: string; last_checked?: string };
  tiles?: Array<{
    key?: string;
    label?: string;
    value?: string | number;
    sub?: string;
    tone?: string;
    suffix?: string;
  }>;
  summary?: Array<{ label?: string; body?: string }>;
}

export const KEYWORDS_PAGE_SIZE = 25;

export interface KeywordsPagination {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface KeywordsData {
  items: KrKeyword[];
  stats: KrStat[];
  measuredFrom?: KrMeasuredFrom;
  summary: KrSummaryItem[];
  pagination: KeywordsPagination;
}

export interface KeywordBriefResult {
  detail?: KrKeyword['detail'];
  generating: boolean;
  jobId?: string;
}

const KR_CATEGORIES: KrCategory[] = [
  'General',
  'Emergency',
  'Cosmetic',
  'Implants',
  'Spanish',
  'Specialty',
];

const STAT_TONE_BY_KEY: Record<string, KrStat['tone']> = {
  page1: 'neutral',
  top3: 'success',
  not_ranking: 'terra',
  notranking: 'terra',
  improving: 'success',
};

function adaptCategory(raw: string | undefined): KrCategory {
  const category = str(raw, 'General');
  return KR_CATEGORIES.includes(category as KrCategory) ? (category as KrCategory) : 'General';
}

function adaptStatTone(key: string, tone?: string): KrStat['tone'] {
  const normalized = str(tone, '').toLowerCase();
  if (normalized === 'success') return 'success';
  if (normalized === 'gold' || normalized === 'warning') return 'gold';
  if (normalized === 'terra' || normalized === 'error' || normalized === 'danger') return 'terra';
  return STAT_TONE_BY_KEY[key.toLowerCase()] ?? 'neutral';
}

function adaptTileValue(value: unknown): string {
  if (value == null) return '—';
  return String(value);
}

function adaptRank(row: BeKeyword): string {
  const rawRank = row.rank;
  if (typeof rawRank === 'string') {
    const text = rawRank.trim();
    if (!text) return 'Not on page 1';
    if (text.toLowerCase().includes('not on page')) return 'Not on page 1';
    return text;
  }

  const position =
    row.your_position ??
    (typeof rawRank === 'number' ? rawRank : null) ??
    row.position ??
    null;

  if (position == null) return 'Not on page 1';
  if (position > 10) return 'Not on page 1';
  return String(position);
}

function adaptVolume(row: BeKeyword): string {
  if (typeof row.volume === 'string' && row.volume.trim()) return row.volume.trim();
  if (typeof row.volume === 'number') return row.volume.toLocaleString();
  if (row.search_volume_monthly != null) return row.search_volume_monthly.toLocaleString();
  if (row.search_volume != null) return num(row.search_volume).toLocaleString();
  return '—';
}

function adaptLeader(row: BeKeyword): string {
  if (row.leader?.trim()) return row.leader.trim();
  if (row.best_competitor?.name?.trim()) return row.best_competitor.name.trim();
  return '—';
}

function adaptKeyword(row: BeKeyword): KrKeyword | null {
  const keyword = str(row.keyword, '').trim();
  const id = str(row.id ?? keyword, '').trim();
  if (!keyword || !id) return null;

  return {
    id,
    keyword,
    category: adaptCategory(row.category),
    volume: adaptVolume(row),
    leader: adaptLeader(row),
    rank: adaptRank(row),
    delta: num(row.delta ?? row.your_position_change_30d),
  };
}

function adaptBrief(be: BeKeywordBrief) {
  return {
    comparedTo: str(be.compared_to, '—'),
    bottomLine: str(be.bottom_line, '—'),
    doingRight: be.doing_right ?? [],
    gaps: be.gaps ?? [],
    playbook: be.playbook ?? [],
    serp: (be.serp ?? []).map((row) => {
      const c = deepCamel<Record<string, unknown>>(row);
      return {
        position: num(c.position),
        url: str(c.url),
        title: str(c.title),
        ...(c.snippet ? { snippet: str(c.snippet) } : {}),
        you: Boolean(c.you),
      };
    }),
  };
}

function adaptMeasuredFrom(summary: BeKeywordSummary | undefined): KrMeasuredFrom | undefined {
  const measured = summary?.measured_from;
  if (!measured?.location && !measured?.last_checked) return undefined;
  return {
    location: str(measured.location, '—'),
    lastChecked: measured.last_checked ? relativeTimeFromIso(measured.last_checked) : '—',
  };
}

function adaptStats(summary: BeKeywordSummary | undefined): KrStat[] {
  return (summary?.tiles ?? []).map((tile) => {
    const key = str(tile.key, 'stat');
    return {
      key,
      label: str(tile.label),
      value: adaptTileValue(tile.value),
      sub: str(tile.sub, ''),
      tone: adaptStatTone(key, tile.tone),
      ...(tile.suffix ? { suffix: str(tile.suffix) } : {}),
    };
  });
}

function adaptSummaryItems(summary: BeKeywordSummary | undefined): KrSummaryItem[] {
  return (summary?.summary ?? []).map((row) => ({
    label: str(row.label),
    body: str(row.body),
  }));
}

function adaptPagination(
  meta: MarketingMeta | undefined,
  page: number,
  pageSize: number,
  itemCount: number,
): KeywordsPagination {
  const totalCount = num(meta?.total_count, itemCount);
  const totalPages = Math.max(1, num(meta?.total_pages, Math.ceil(totalCount / pageSize)));
  return {
    page: num(meta?.page, page),
    pageSize: num(meta?.page_size, pageSize),
    totalCount,
    totalPages,
  };
}

export async function fetchKeywords(options?: {
  category?: string;
  page?: number;
  limit?: number;
}): Promise<KeywordsData> {
  const page = options?.page ?? 1;
  const limit = options?.limit ?? KEYWORDS_PAGE_SIZE;
  const params: Record<string, string | number> = { page, limit };
  if (options?.category && options.category !== 'All') params.category = options.category;

  const [{ items: rows, meta }, summary] = await Promise.all([
    growthList<BeKeyword>('/keyword-rankings/', params),
    growthGet<BeKeywordSummary>('/keyword-rankings/summary/').catch(() => undefined),
  ]);

  const items = rows.map(adaptKeyword).filter((row): row is KrKeyword => row != null);
  const measuredFrom = adaptMeasuredFrom(summary);

  return {
    items,
    stats: adaptStats(summary),
    ...(measuredFrom ? { measuredFrom } : {}),
    summary: adaptSummaryItems(summary),
    pagination: adaptPagination(meta, page, limit, items.length),
  };
}

export async function fetchKeywordBrief(id: string): Promise<KeywordBriefResult> {
  const { data: be, status } = await growthGetAccepted<
    BeKeywordBrief & { brief?: BeKeywordBrief } & MarketingJobData
  >(`/keyword-rankings/${encodeURIComponent(id)}/strategy-brief/`);

  if (status === 202) {
    return {
      generating: true,
      ...(be.job_id ? { jobId: be.job_id } : {}),
    };
  }

  const brief = adaptBrief(be.brief ?? be);
  return {
    generating: false,
    detail: { serp: brief.serp, brief },
  };
}

export async function regenerateStrategyBrief(id: string) {
  return growthPostAsync(
    `/keyword-rankings/${encodeURIComponent(id)}/strategy-brief/regenerate/`,
  );
}

export async function fetchKeywordWithBrief(id: string): Promise<KrKeyword | null> {
  const { items } = await fetchKeywords();
  const keyword = items.find((k) => k.id === id);
  if (!keyword) return null;
  try {
    const result = await fetchKeywordBrief(id);
    if (result.detail) return { ...keyword, detail: result.detail };
    return keyword;
  } catch {
    return keyword;
  }
}
